/**
 * Debounce helper — delegates to {@link Utility.debounce} (theme-wide consistent timing).
 * @param {Function} fn
 * @param {number} wait
 * @returns {Function}
 */
const debounce = (fn, wait) => Utility.debounce(fn, wait);

/**
 * POST config for cart / JSON endpoints — delegates to {@link Utility.Fetch.config}.
 * @param {'json'|'javascript'} [type='json']
 * @returns {{ method: string, headers: HeadersInit, body?: * }}
 */
const fetchConfig = (type = 'json') => Utility.Fetch.config(type);

/** Debounce delay (ms) for cart quantity inputs and similar. */
const ON_CHANGE_DEBOUNCE_TIMER = 300;

/**
 * Open Modal javascript trigger
 */
function openModal(modal, opener){
  if(modal && typeof modal.show === 'function') {
    try {
      modal.show(opener);
    } catch (err) {
      console.error('Modal open error:', err);
      if (typeof ThemeToast !== 'undefined' && ThemeToast.error) {
        ThemeToast.error(err?.message || 'Could not open dialog', 'Modal');
      }
    }
  }
}
/**
* background Overlay
*/
class SiteOverlay extends HTMLElement{
  constructor() {
    super();
    this.overlayElement = document.getElementById('site-overlay');
    this.bindEvents();
  }

  /**
   * bind Click and Touchend event to this custom component
   */
  bindEvents(){
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('touchend', this.onClick.bind(this));
  }

  /**
   * Click event of Site overlay
   */
  onClick(){
    SiteOverlay.hideOverlay();
  }

  /**
   * Show Overlay
   * @returns {boolean} Returns true if overlay was shown, false if already visible
   */
   static showOverlay(){
    const overlay = document.getElementById('site-overlay');
    if (overlay.classList.contains('overlay--body')) {
      return false; // Already visible
    }
    
    overlay.classList.add('overlay--body');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.add('scroll-fixed');
    
    // Make background content inert (prevents focus and interaction)
    // const mainContent = document.querySelector('main');
    // const header = document.querySelector('header');
    // if (mainContent) mainContent.setAttribute('inert', '');
    // if (header) header.setAttribute('inert', '');
    
    return true; // Successfully shown
  }

  /**
   * Hide overlay
   * @returns {boolean} Returns true if overlay was hidden, false if already hidden
   */
  static hideOverlay(){
    const overlay = document.getElementById('site-overlay');
    if (!overlay.classList.contains('overlay--body')) {
      return false; // Already hidden
    }    
    
    const openDrawers = Array.from(document.querySelectorAll('custom-drawer.open'));
    openDrawers.forEach(drawer => {
      drawer.closeDrawer();
    });

    // Cart drawer is a separate component (`cart-drawer.active`), so close it explicitly.
    const activeCartDrawers = Array.from(document.querySelectorAll('cart-drawer.active'));
    activeCartDrawers.forEach((drawer) => {
      drawer.classList.remove('active');
    });
    
    overlay.classList.remove('overlay--body');
    document.body.classList.remove('scroll-fixed');
    
    // Remove inert from background content
    // const mainContent = document.querySelector('main');
    // const header = document.querySelector('header');
    // if (mainContent) mainContent.removeAttribute('inert');
    // if (header) header.removeAttribute('inert');
    
    return true; // Successfully hidden
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick.bind(this));
    this.removeEventListener('touchend', this.onClick.bind(this));
  }
}
customElements.define("site-overlay", SiteOverlay, {
  once: true
});

/**
* Modal Dialogue
*
*/
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.modal = this.querySelector('.modal');
    this.modalOpeners = Array.from(document.querySelectorAll(`[data-modal-opener="${this.id}"]`));
    this.closeBtn = this.querySelector('[id^="ModalClose-"]');

    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
  }

  connectedCallback() {
    this.modalOpeners.forEach((opener)=>{
      opener.addEventListener('click', (evt)=>{
        evt.preventDefault();
        this.show(opener);
      });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.hide.bind(this));
    }

    this.addEventListener('click', (evt) => {
      if (evt.target === this) this.hide();
    });

    this.addEventListener('keyup', (evt) => {
      if (evt.code && evt.code.toUpperCase() === 'ESCAPE') this.hide();
    });
  }

  // Clean up event listeners
  disconnectedCallback() {
    this.isOpen = false;

    this.modalOpeners.forEach((opener) => {
      opener.removeEventListener('click', this.show.bind(this));
    });

    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.hide.bind(this));
    }

    this.removeEventListener('click', this.hide);
    this.removeEventListener('keyup', this.hide);
  }

  /**
   * Open the Modal
   * @param {element} opener 
   * @returns {ModalDialog} Returns this for method chaining
   */
  show(opener) {
    if (this.isOpen) return this; // Prevent opening if already open

    this.isOpen = true;
    this.openedBy = opener;
    this.modal.classList.add('open');
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');

    SiteOverlay.showOverlay();
    
    // Announce modal opening with specific name
    const modalName = this.getAttribute('aria-label') || 'Modal Dialogue';
    Utility.announceToScreenReader(`${modalName} modal opened`);
    
    Utility.Focus.trap(this, this.closeBtn);
    
    return this; // Enable method chaining
  }

  /**
   * Close Modal Dialogue
   * @returns {ModalDialog} Returns this for method chaining
   */
  hide() {
    if (!this.isOpen) return this; // Prevent closing if already closed

    this.isOpen = false;
    this.modal.classList.remove('open');
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');

    SiteOverlay.hideOverlay();
    
    // Announce modal closing with specific name
    const modalName = this.getAttribute('aria-label') || 'Modal Dialogue';
    Utility.announceToScreenReader(`${modalName} modal closed`);
    
    Utility.Focus.release(this.openedBy);
    if(this.id == 'PopupModal-video') {
      this.querySelector('.video-container').innerHTML = '';
    }
    
    return this; // Enable method chaining
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
 * Custom Drawer Component
 * A flexible drawer component that slides in from various positions
 * @extends HTMLElement
 */
class CustomDrawer extends HTMLElement {
  constructor() {
    super();
    
    this.isOpen = false;
    this.openedBy = null;
    this.toggleDrawer = Array.from(document.querySelectorAll(`[data-toggleDrawer="${this.id}"]`));
    this.closeButtons = Array.from(this.querySelectorAll('[data-closedrawer]'));
    this.forceFocus = this.querySelector('h2, [tabindex="0"]') ||
                     this.querySelector('button, a, input');

    /**
     * Only toggle `inert` when it is present in markup. Drawers without `inert` must not get it from JS.
     */
    this.manageInert = this.hasAttribute('inert');

    // Set initial ARIA attributes
    this.setAttribute('aria-hidden', 'true');

    if (this.manageInert) {
      this.setAttribute('inert', '');
    }
  }

  connectedCallback() {
    // Bind toggle drawer triggers
    this.toggleDrawer.forEach((trigger) => {
      trigger.addEventListener('click', (evt) => {
        evt.preventDefault();
        if (this.isOpen) {
          this.closeDrawer();
        } else {
          this.openDrawer(trigger);
        }
      });
    });

    // Bind close buttons
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.closeDrawer();
      });
    });

    // Close on Escape key
    this.addEventListener('keyup', (evt) => {
      if (evt.code && evt.code.toUpperCase() === 'ESCAPE' && this.isOpen) {
        this.closeDrawer();
      }
    });
  }

  disconnectedCallback() {
    this.toggleDrawer.forEach((trigger) => {
      trigger.removeEventListener('click', this.openDrawer);
    });
    
    this.closeButtons.forEach((btn) => {
      btn.removeEventListener('click', this.closeDrawer);
    });
    
    this.removeEventListener('keyup', this.closeDrawer);
  }

  /**
   * Open the drawer
   * @param {HTMLElement} opener - Element that triggered the drawer
   * @returns {CustomDrawer} Returns this for method chaining
   */
  openDrawer(opener) {
    if (this.isOpen) return this;

    this.isOpen = true;
    this.openedBy = opener;
    
    // Close other drawers first
    this.resetOtherDrawers();
    
    // Show drawer
    this.classList.add('open');
    if (this.manageInert) {
      this.removeAttribute('inert');
    }
    this.setAttribute('aria-hidden', 'false');
    
    // Show overlay
    SiteOverlay.showOverlay();
    
    // Trap focus
    Utility.Focus.trap(this, this.forceFocus);
    
    // Announce to screen reader
    const drawerName = this.getAttribute('aria-label') || 'Drawer';
    Utility.announceToScreenReader(`${drawerName} drawer opened`);
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true }));
    
    return this;
  }

  /**
   * Close the drawer
   * @returns {CustomDrawer} Returns this for method chaining
   */
  closeDrawer() {
    if (!this.isOpen) return this;

    this.isOpen = false;
    
    // Hide drawer
    this.classList.remove('open');
    if (this.manageInert) {
      this.setAttribute('inert', '');
    }
    this.setAttribute('aria-hidden', 'true');
    
    // Hide overlay (only if no other drawers are open)
    const otherOpenDrawers = Array.from(document.querySelectorAll('custom-drawer.open'));
    if (otherOpenDrawers.length === 0) {
      SiteOverlay.hideOverlay();
    }
    
    // Release focus trap and return focus
    Utility.Focus.release(this.openedBy);
    
    // Announce to screen reader
    const drawerName = this.getAttribute('aria-label') || 'Drawer';
    Utility.announceToScreenReader(`${drawerName} drawer closed`);
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
    
    this.openedBy = null;
    
    return this;
  }

  /**
   * Close all other open drawers
   * @returns {CustomDrawer} Returns this for method chaining
   */
  resetOtherDrawers() {
    const allDrawers = Array.from(document.querySelectorAll('custom-drawer'));
    allDrawers.forEach((drawer) => {
      if (drawer !== this && drawer.isOpen) {
        drawer.closeDrawer();
      }
    });
    return this;
  }

  /**
   * Hide open panels/dropdowns inside drawer
   * @returns {CustomDrawer} Returns this for method chaining
   */
  hideOpenPanels() {
    const openPanels = Array.from(this.querySelectorAll('.open, [aria-expanded="true"]'));
    openPanels.forEach((panel) => {
      panel.classList.remove('open');
      panel.setAttribute('aria-expanded', 'false');
    });
    return this;
  }
}

// Register custom element
customElements.define('custom-drawer', CustomDrawer);

/**
 * Individual Toast Notification Element
 * Represents a single toast notification instance
 * @extends HTMLElement
 */
class ToastNotification extends HTMLElement {
  /**
   * Constructor - Initialize toast notification properties
   */
  constructor() {
    super();

    // DOM element references (initialized after innerHTML is set)
    this.container = null;
    this.closeBtn = null;
    this.progressBar = null;
    
    // Animation state
    this.animationFrameId = null; // Request ID from requestAnimationFrame
    this.animationCallback = null; // Animation function reference
    
    // Unique identifier for this toast
    this.toastId = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Close button click handler (bound for proper cleanup)
    this.boundHandleClose = this.hide.bind(this);
  }

  /**
   * Initialize DOM element references
   * Called after innerHTML is set and element is connected to DOM
   * @private
   */
  _initializeElements() {
    if (!this.container) {
      this.container = this.querySelector('.snotifyToast');
      this.closeBtn = this.querySelector('.snotifyToast__close');
      this.progressBar = this.querySelector('[data-toast-pr]');
      
      // Set ARIA role for accessibility
      if (this.container) {
        this.container.setAttribute('role', 'alert');
      }
    }
  }

  /**
   * Lifecycle callback - Called when element is added to DOM
   */
  connectedCallback() {
    // Initialize DOM references when connected
    this._initializeElements();
    
    // Attach close button event listener
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.boundHandleClose);
    }
  }

  /**
   * Lifecycle callback - Called when element is removed from DOM
   */
  disconnectedCallback() {
    // Remove event listeners
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.boundHandleClose);
    }
    
    // Cancel any running animations
    this._cancelAnimation();
  }

  /**
   * Cancel any active animation frame
   * @private
   */
  _cancelAnimation() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.animationCallback = null;
  }

  /**
   * Show the toast notification with content
   * @param {string} title - Notification title text
   * @param {string} body - Notification body text
   * @param {object} options - Configuration options
   * @param {string} options.type - Notification type: 'success', 'error', 'warning', 'info'
   * @param {number} options.timeout - Auto-dismiss timeout in milliseconds
   * @returns {ToastNotification} Returns this for method chaining
   */
  show(title, body, options = {}) {
    // Ensure DOM elements are initialized
    this._initializeElements();
    
    // Validate container exists
    if (!this.container) {
      console.error('ToastNotification: Container element not found');
      if (typeof ThemeToast !== 'undefined' && ThemeToast.error) {
        ThemeToast.error('Toast container not found', 'Toast');
      }
      return this;
    }

    // Update ARIA attributes for visibility
    this.setAttribute('aria-hidden', 'false');
    this.removeAttribute('inert');

    // Get notification type and timeout with defaults
    const type = options.type || 'info';
    const timeout = options.timeout || 5000;

    // Apply notification type class
    this.container.classList.add('open', `snotify-${type}`);
    
    // Update title and body content
    const titleEl = this.querySelector('.snotifyToast__title');
    const bodyEl = this.querySelector('.snotifyToast__body');
    
    if (titleEl) {
      titleEl.textContent = title || '';
    }
    if (bodyEl) {
      bodyEl.textContent = body || '';
    }

    // Start auto-dismiss timer with progress bar
    this._manageToggle(timeout);
    
    return this;
  }

  /**
   * Manage auto-dismiss timer and progress bar animation
   * @param {number} timeout - Dismiss timeout in milliseconds
   * @private
   */
  _manageToggle(timeout) {
    // Get progress bar element
    if (!this.progressBar) {
      this.progressBar = this.querySelector('[data-toast-pr]');
    }
    
    if (!this.progressBar) {
      return;
    }

    // Cancel any existing animation first
    this._cancelAnimation();

    // Calculate animation frames (60fps = 16.67ms per frame, divided by 2 for smoother animation)
    const totalFrames = Math.ceil(timeout / 16.67) / 2;
    let frame = 0;
    let width = 0;

    // Create animation callback function
    const animate = () => {
      width = (frame / totalFrames) * 100;
      
      if (this.progressBar) {
        this.progressBar.setAttribute('style', `width: ${width}%`);
      }
      
      if (frame < totalFrames) {
        frame++;
        // Request next frame and store the request ID
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation complete, hide the toast
        this.animationFrameId = null;
        this.animationCallback = null;
        this.hide();
      }
    };

    // Store callback reference for potential cancellation
    this.animationCallback = animate;
    
    // Start animation and store the request ID
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Hide and remove the toast notification
   * @returns {ToastNotification} Returns this for method chaining
   */
  hide() {
    // Cancel progress bar animation
    this._cancelAnimation();
    
    // Update ARIA attributes for hidden state
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');

    // Ensure elements are initialized
    this._initializeElements();
    
    // Reset container classes
    if (this.container) {
      this.container.className = 'snotifyToast';
    }
    
    // Clear content
    const titleEl = this.querySelector('.snotifyToast__title');
    const bodyEl = this.querySelector('.snotifyToast__body');
    
    if (titleEl) {
      titleEl.textContent = '';
    }
    if (bodyEl) {
      bodyEl.textContent = '';
    }
    
    // Reset progress bar
    if (this.progressBar) {
      this.progressBar.removeAttribute('style');
    }

    // Notify parent container to remove from tracking array
    if (this.parentNode && typeof this.parentNode.removeToast === 'function') {
      this.parentNode.removeToast(this);
    }

    // Remove from DOM after slide-out animation completes
    setTimeout(() => {
      if (this.parentNode) {
        this.parentNode.removeChild(this);
      }
    }, 400);

    return this;
  }
}

/**
 * Toast Container - Manages multiple toast notifications
 * Handles creation, stacking, and removal of toast instances
 * @extends HTMLElement
 */
class ToastContainer extends HTMLElement {
  /**
   * Constructor - Initialize toast container
   */
  constructor() {
    super();
    
    // Array to track active toast instances
    this.toasts = [];
    
    // Maximum number of simultaneous toasts (oldest removed when limit reached)
    this.maxToasts = 5;
  }

  /**
   * Create and show a new toast notification
   * @param {string} title - Notification title text
   * @param {string} body - Notification body text
   * @param {object} options - Configuration options
   * @param {string} options.type - Notification type: 'success', 'error', 'warning', 'info'
   * @param {number} options.timeout - Auto-dismiss timeout in milliseconds
   * @returns {ToastNotification} The created toast instance
   */
  show(title, body, options = {}) {
    // Remove oldest toast if we've reached the maximum limit
    if (this.toasts.length >= this.maxToasts) {
      const oldestToast = this.toasts.shift();
      if (oldestToast && typeof oldestToast.hide === 'function') {
        oldestToast.hide();
      }
    }

    // Create new toast element
    const toast = this._createToastElement();
    
    // Append to DOM (triggers connectedCallback)
    this.appendChild(toast);
    
    // Track in array
    this.toasts.push(toast);

    // Show the toast with content (use setTimeout to ensure DOM is ready)
    setTimeout(() => {
      if (toast && typeof toast.show === 'function') {
        toast.show(title, body, options);
      }
    }, 0);

    // Update positioning for all toasts
    this._updatePositions();

    return toast;
  }

  /**
   * Create a new toast notification element with HTML structure
   * @returns {HTMLElement} Toast element ready to be appended to DOM
   * @private
   */
  _createToastElement() {
    // Create the custom element
    const toast = document.createElement('toast-notification');
    
    // Set ARIA attributes for accessibility
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.setAttribute('aria-atomic', 'true');
    toast.setAttribute('aria-hidden', 'true');
    toast.setAttribute('inert', '');

    // Icon HTML templates (all icons included, CSS shows appropriate one based on type)
    const iconHTML = `
      <div class="snotify-icon-error" style="display: none;">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
          <g><g><path d="M64,0C28.7,0,0,28.7,0,64s28.7,64,64,64s64-28.7,64-64S99.3,0,64,0z M64,120C33.1,120,8,94.9,8,64S33.1,8,64,8s56,25.1,56,56S94.9,120,64,120z"/></g></g>
          <path d="M41.4,91.1C41.4,91.1,41.4,91.1,41.4,91.1c1.2,0,2.3-0.5,3.2-1.3L64,70.4l19.4,19.4c0.9,0.8,2,1.3,3.2,1.3c0,0,0,0,0,0c1.2,0,2.3-0.5,3.2-1.3c0.9-0.9,1.3-2,1.3-3.2c0-1.2-0.5-2.3-1.3-3.2L70.4,64l19.4-19.4c0.9-0.9,1.3-2,1.3-3.2c0-1.2-0.5-2.3-1.3-3.2c-0.9-0.9-2-1.3-3.2-1.3c0,0,0,0,0,0c-1.2,0-2.3,0.5-3.2,1.3L64,57.6L44.6,38.2c-0.9-0.9-2-1.3-3.2-1.3c0,0,0,0,0,0c-1.2,0-2.3,0.5-3.2,1.3c-0.9,0.9-1.3,2-1.3,3.2c0,1.2,0.5,2.3,1.3,3.2L57.6,64L38.2,83.4c-0.9,0.9-1.3,2-1.3,3.2c0,1.2,0.5,2.3,1.3,3.2C39,90.7,40.2,91.1,41.4,91.1z"/>
        </svg>
      </div>
      <div class="snotify-icon-info" style="display: none;">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26">
          <g><g><path d="M13,26C5.8,26,0,20.2,0,13S5.8,0,13,0s13,5.8,13,13S20.2,26,13,26z M13,2C6.9,2,2,6.9,2,13s4.9,11,11,11s11-4.9,11-11S19.1,2,13,2z"/></g></g>
          <path d="M11.8,18.9h2.4v-7.1h-2.4V18.9L11.8,18.9z M11.8,9.5h2.4V7.1h-2.4V9.5L11.8,9.5z"/>
        </svg>
      </div>
      <div class="snotify-icon-warning" style="display: none;">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.7 19.7">
          <g><path d="M9.8,3.1c0.6,0,1.1,0.4,1.5,1.1l5.9,9.9c0.9,1.4,0.2,2.6-1.5,2.6h-12c-1.7,0-2.3-1.2-1.5-2.6l5.9-9.9C8.7,3.4,9.3,3.1,9.8,3.1 M9.8,1.1c-1.3,0-2.5,0.7-3.3,2L0.7,13c-0.8,1.3-0.9,2.7-0.2,3.8s1.9,1.8,3.4,1.8h12c1.5,0,2.8-0.6,3.4-1.8s0.6-2.5-0.2-3.8l-5.9-9.9C12.3,1.8,11.1,1.1,9.8,1.1z"/></g>
          <g><circle cx="9.8" cy="13.6" r="1.3"/></g>
          <g><path d="M11.3,7.6c0-0.8-0.7-1.5-1.5-1.5S8.3,6.7,8.3,7.6c0,0.2,0,0.4,0.1,0.6c0.6,1.4,1.4,3.4,1.4,3.4s0.8-2.1,1.4-3.4C11.3,8,11.3,7.8,11.3,7.6z"/></g>
        </svg>
      </div>
      <div class="snotify-icon-success" style="display: none;">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26">
          <g><g><path d="M12.2,16.7c-0.3,0-0.5-0.1-0.7-0.3L9,14c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l1.8,1.8L17,9.5c0.4-0.4,1-0.4,1.4,0s0.4,1,0,1.4l-5.5,5.5C12.7,16.7,12.4,16.7,12.2,16.7z"/></g>
          <g><path d="M13,26C5.8,26,0,20.2,0,13S5.8,0,13,0s13,5.8,13,13S20.2,26,13,26z M13,2C6.9,2,2,6.9,2,13s4.9,11,11,11s11-4.9,11-11S19.1,2,13,2z"/></g></g>
        </svg>
      </div>
    `;

    // Set innerHTML with toast structure
    toast.innerHTML = `
      <div class="snotifyToast" role="dialog" aria-labelledby="snotifyToast__title" aria-describedby="snotifyToast__body">
        <div class="snotifyToast-dialog">
          <div class="snotifyToast__progressBar">
            <span class="snotifyToast__progressBar__percentage" data-toast-pr></span>
          </div>
          <div class="snotifyToast__inner">
            <div class="snotify-icon">
              ${iconHTML}
            </div>
            <div class="snotifyToast__content">
              <div class="snotifyToast__title" id="snotifyToast__title"></div>
              <div class="snotifyToast__body" id="snotifyToast__body"></div>
            </div>
            <button type="button" class="snotifyToast__close snotifyToast__buttons--bold" aria-label="Close toast notification"></button>
          </div>
        </div>
      </div>
    `;

    // Note: _initializeElements() will be called automatically in connectedCallback
    // after the element is appended to the DOM

    return toast;
  }

  /**
   * Update CSS custom properties for toast positioning
   * Used for potential future stacking animations or z-index management
   * @private
   */
  _updatePositions() {
    // Set CSS custom property for each toast's index
    // This can be used for z-index or transform calculations if needed
    this.toasts.forEach((toast, index) => {
      if (toast && toast.style) {
        toast.style.setProperty('--toast-index', index);
      }
    });
  }

  /**
   * Remove toast from tracking array
   * Called by toast instance when it hides itself
   * @param {ToastNotification} toast - The toast instance to remove
   */
  removeToast(toast) {
    const index = this.toasts.indexOf(toast);
    if (index > -1) {
      this.toasts.splice(index, 1);
      // Update positions after removal
      this._updatePositions();
    }
  }

  /**
   * Hide all active toast notifications
   * @returns {ToastContainer} Returns this for method chaining
   */
  hideAll() {
    // Create a copy of the array to avoid issues during iteration
    // (toasts may remove themselves from array during hide())
    const toastsCopy = [...this.toasts];
    
    // Hide each toast
    toastsCopy.forEach((toast) => {
      if (toast && typeof toast.hide === 'function') {
        toast.hide();
      }
    });
    
    // Clear the array
    this.toasts = [];
    
    return this;
  }

  /**
   * Backward compatibility method - Alias for show()
   * @param {string} title - Notification title text
   * @param {string} body - Notification body text
   * @param {object} options - Configuration options
   * @returns {ToastContainer} Returns this for method chaining
   */
  updateNotification(title, body, options = {}) {
    this.show(title, body, options);
    return this;
  }

  /**
   * Hide the most recent toast notification
   * Backward compatibility method
   * @returns {ToastContainer} Returns this for method chaining
   */
  hide() {
    if (this.toasts.length > 0) {
      const lastToast = this.toasts[this.toasts.length - 1];
      if (lastToast && typeof lastToast.hide === 'function') {
        lastToast.hide();
      }
    }
    return this;
  }
}

// Register custom elements
customElements.define('toast-notification', ToastNotification);
customElements.define('toast-container', ToastContainer);

/**
 * Initialize the global toast notification container
 * Sets up window.notificationEle for global access
 */
const initToastContainer = () => {
  // Try to find existing container in DOM
  const container = document.querySelector('toast-container');
  
  if (container) {
    // Use existing container
    window.notificationEle = container;
  } else {
    // Fallback: create container if it doesn't exist in DOM
    const fallbackContainer = document.createElement('toast-container');
    fallbackContainer.setAttribute('role', 'region');
    fallbackContainer.setAttribute('aria-label', 'Toast notifications');
    fallbackContainer.setAttribute('aria-live', 'polite');
    document.body.appendChild(fallbackContainer);
    window.notificationEle = fallbackContainer;
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initToastContainer);
} else {
  // DOM already loaded, initialize immediately
  initToastContainer();
}

/**
 * Theme Header Component
 * Manages header functionality including mobile navigation toggle and sticky behavior
 * @extends HTMLElement
 */
class ThemeHeader extends HTMLElement {
  constructor() {
    super();
    
    // DOM element references
    this.mobileMenuToggle = null;
    this.mobileNavDrawer = null;
    this.header = this.closest('header') || this;
    
    // Sticky header state
    this.stickyType = this.getAttribute('data-sticky-type') || 'none';
    this.headerIsAlwaysSticky = this.stickyType === 'always';
    this.headerBounds = null;
    this.currentScrollTop = 0;
    this.animationFrame = null;
    this.observer = null;
    
    // State
    this.isMenuOpen = false;
    
    // Bind methods
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
    this.handleDrawerClose = this.handleDrawerClose.bind(this);
    this.onScrollHandler = this.onScroll.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  /**
   * Lifecycle callback - Called when element is added to DOM
   */
  connectedCallback() {
    // Find mobile menu toggle button
    this.mobileMenuToggle = this.querySelector('[data-mobile-menu-toggle]');
    
    // Find mobile navigation drawer
    const drawerId = this.mobileMenuToggle?.getAttribute('data-toggleDrawer') || 
                     this.mobileMenuToggle?.getAttribute('data-mobile-menu-toggle');
    if (drawerId) {
      this.mobileNavDrawer = document.getElementById(drawerId);
    }
    
    // Bind events
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.addEventListener('click', this.handleMenuToggle);
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
      this.mobileMenuToggle.setAttribute('aria-controls', drawerId || 'mobile-nav-drawer');
    }
    
    // Listen for drawer close events
    if (this.mobileNavDrawer) {
      this.mobileNavDrawer.addEventListener('drawer:closed', this.handleDrawerClose);
    }
    
    // Initialize desktop navigation dropdowns
    this.initDesktopDropdowns();

    this.initMegamenu3HoverImages();
    
    // Initialize sticky header if enabled
    if (this.stickyType !== 'none') {
      this.initStickyHeader();
    }
  }

  /**
   * Lifecycle callback - Called when element is removed from DOM
   */
  disconnectedCallback() {
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.removeEventListener('click', this.handleMenuToggle);
    }
    
    if (this.mobileNavDrawer) {
      this.mobileNavDrawer.removeEventListener('drawer:closed', this.handleDrawerClose);
    }
    
    // Cleanup sticky header
    if (this.stickyType !== 'none') {
      this.cleanupStickyHeader();
    }
  }

  /**
   * Initialize sticky header functionality
   */
  initStickyHeader() {
    if (!this.header) return;
    
    // Set initial scroll position
    this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Add sticky class to header
    this.header.classList.add('header-sticky');
    
    // If always sticky, just add the class and return
    if (this.headerIsAlwaysSticky) {
      return;
    }
    
    // For on-scroll-up mode, set up scroll listener and observer
    this.createObserver();
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  /**
   * Cleanup sticky header listeners
   */
  cleanupStickyHeader() {
    window.removeEventListener('scroll', this.onScrollHandler);
    
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    if (this.observer && this.header) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Create Intersection Observer to track header bounds
   */
  createObserver() {
    if (!this.header || !window.IntersectionObserver) return;
    
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0]) {
        this.headerBounds = entries[0].boundingClientRect;
      }
    }, {
      threshold: [0, 1]
    });
    
    this.observer.observe(this.header);
    
    // Set initial bounds
    if (this.header) {
      this.headerBounds = this.header.getBoundingClientRect();
    }
  }

  /**
   * Handle scroll event with requestAnimationFrame optimization
   */
  onScroll() {
    if (!this.animationFrame) {
      this.animationFrame = requestAnimationFrame(() => {
        this.handleScroll();
        this.animationFrame = null;
      });
    }
  }

  /**
   * Process scroll position and update header state
   */
  handleScroll() {
    if (!this.header || this.headerIsAlwaysSticky) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const headerHeight = this.headerBounds?.height || this.header.offsetHeight;
    
    // At page top - reset to normal position
    if (scrollTop <= headerHeight) {
      this.reset();
    } 
    // Scrolling down - hide header
    else if (scrollTop > this.currentScrollTop && scrollTop > headerHeight) {
      this.hide();
    } 
    // Scrolling up - reveal header
    else if (scrollTop < this.currentScrollTop && scrollTop > headerHeight) {
      this.reveal();
    }
    
    this.currentScrollTop = scrollTop;
  }

  /**
   * Hide header on scroll down
   */
  hide() {
    if (!this.header) return;
    this.header.classList.add('header-hidden');
    this.header.classList.remove('animate');
    this.dispatchEvent(new CustomEvent('header:hidden', { bubbles: true }));
  }

  /**
   * Reveal header on scroll up
   */
  reveal() {
    if (!this.header) return;
    this.header.classList.remove('header-hidden');
    this.header.classList.add('animate');
    this.dispatchEvent(new CustomEvent('header:revealed', { bubbles: true }));
  }

  /**
   * Reset header to normal position
   */
  reset() {
    if (!this.header) return;
    this.header.classList.remove('header-hidden', 'animate');
    this.dispatchEvent(new CustomEvent('header:reset', { bubbles: true }));
  }

  /**
   * Initialize desktop navigation dropdowns
   * Supports hover for desktop and click for touch devices
   */
  initDesktopDropdowns() {
    const dropdowns = Array.from(this.querySelectorAll('[data-nav-dropdown]'));
    
    // Detect touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Helper function to close all dropdowns except the specified one
    const closeOtherDropdowns = (currentDropdown) => {
      dropdowns.forEach(otherDropdown => {
        if (otherDropdown !== currentDropdown) {
          otherDropdown.classList.remove('open');
          const otherLink = otherDropdown.querySelector('[data-nav-link-parent]');
          if (otherLink) {
            otherLink.setAttribute('aria-expanded', 'false');
          }
        }
      });
    };
    
    // Helper function to close all nested dropdowns
    const closeAllNestedDropdowns = () => {
      dropdowns.forEach(dropdown => {
        const nestedDropdowns = dropdown.querySelectorAll('[data-nav-dropdown-nested]');
        nestedDropdowns.forEach(nestedDropdown => {
          nestedDropdown.classList.remove('open');
          const nestedLink = nestedDropdown.querySelector('[data-nav-link-parent]');
          if (nestedLink) {
            nestedLink.setAttribute('aria-expanded', 'false');
          }
        });
      });
    };
    
    dropdowns.forEach(dropdown => {
      const link = dropdown.querySelector('[data-nav-link-parent]');
      const menu = dropdown.querySelector('[data-nav-dropdown-menu]');
      
      if (!link || !menu) return;
      
      // Set initial aria attributes
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      menu.setAttribute('role', 'menu');
      const menuId = `nav-menu-${Math.random().toString(36).substr(2, 9)}`;
      menu.setAttribute('id', menuId);
      link.setAttribute('aria-controls', menuId);
      
      menu.querySelectorAll('[data-nav-link]').forEach(item => {
        item.setAttribute('role', 'menuitem');
      });
      
      if (isTouchDevice) {
        // Touch devices: Use click/tap
        link.addEventListener('click', (e) => {
          const isOpen = dropdown.classList.contains('open');
          
          if (isOpen) {
            // If already open, allow navigation to parent link
            // Don't prevent default - let the link navigate
            dropdown.classList.remove('open');
            link.setAttribute('aria-expanded', 'false');
            closeAllNestedDropdowns();
          } else {
            // If closed, open dropdown and prevent navigation
            e.preventDefault();
            closeOtherDropdowns(dropdown);
            closeAllNestedDropdowns();
            dropdown.classList.add('open');
            link.setAttribute('aria-expanded', 'true');
          }
        });
      } else {
        // Desktop: Use hover
        dropdown.addEventListener('mouseenter', () => {
          closeOtherDropdowns(dropdown);
          closeAllNestedDropdowns();
          dropdown.classList.add('open');
          link.setAttribute('aria-expanded', 'true');
        });
        
        dropdown.addEventListener('mouseleave', () => {
          dropdown.classList.remove('open');
          link.setAttribute('aria-expanded', 'false');
          closeAllNestedDropdowns();
        });
      }
      
      // Handle keyboard navigation (works for both desktop and touch)
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = dropdown.classList.contains('open');
          if (isOpen) {
            dropdown.classList.remove('open');
            link.setAttribute('aria-expanded', 'false');
            closeAllNestedDropdowns();
          } else {
            closeOtherDropdowns(dropdown);
            closeAllNestedDropdowns();
            dropdown.classList.add('open');
            link.setAttribute('aria-expanded', 'true');
          }
        } else if (e.key === 'Escape') {
          dropdown.classList.remove('open');
          link.setAttribute('aria-expanded', 'false');
          closeAllNestedDropdowns();
          link.focus();
        }
      });
      
      // Handle nested dropdowns
      const nestedDropdowns = dropdown.querySelectorAll('[data-nav-dropdown-nested]');
      nestedDropdowns.forEach(nestedDropdown => {
        const nestedLink = nestedDropdown.querySelector('[data-nav-link-parent]');
        const nestedMenu = nestedDropdown.querySelector('[data-nav-dropdown-menu-nested]');
        
        if (nestedLink && nestedMenu) {
          nestedLink.setAttribute('aria-haspopup', 'true');
          nestedLink.setAttribute('aria-expanded', 'false');
          const nestedMenuId = `nav-menu-nested-${Math.random().toString(36).substr(2, 9)}`;
          nestedMenu.setAttribute('id', nestedMenuId);
          nestedLink.setAttribute('aria-controls', nestedMenuId);
          nestedMenu.setAttribute('role', 'menu');
          nestedMenu.querySelectorAll('[data-nav-link]').forEach(item => {
            item.setAttribute('role', 'menuitem');
          });
          
          if (isTouchDevice) {
            // Touch devices: Use click/tap for nested
            nestedLink.addEventListener('click', (e) => {
              e.stopPropagation(); // Prevent parent dropdown from closing
              const isOpen = nestedDropdown.classList.contains('open');
              
              if (isOpen) {
                // If already open, allow navigation to nested link
                // Don't prevent default - let the link navigate
                nestedDropdown.classList.remove('open');
                nestedLink.setAttribute('aria-expanded', 'false');
              } else {
                // If closed, open nested dropdown and prevent navigation
                e.preventDefault();
                // Close other nested dropdowns in the same parent
                nestedDropdowns.forEach(otherNested => {
                  if (otherNested !== nestedDropdown) {
                    otherNested.classList.remove('open');
                    const otherNestedLink = otherNested.querySelector('[data-nav-link-parent]');
                    if (otherNestedLink) {
                      otherNestedLink.setAttribute('aria-expanded', 'false');
                    }
                  }
                });
                nestedDropdown.classList.add('open');
                nestedLink.setAttribute('aria-expanded', 'true');
              }
            });
          } else {
            // Desktop: Use hover for nested
            nestedDropdown.addEventListener('mouseenter', () => {
              // Close other nested dropdowns in the same parent
              nestedDropdowns.forEach(otherNested => {
                if (otherNested !== nestedDropdown) {
                  otherNested.classList.remove('open');
                  const otherNestedLink = otherNested.querySelector('[data-nav-link-parent]');
                  if (otherNestedLink) {
                    otherNestedLink.setAttribute('aria-expanded', 'false');
                  }
                }
              });
              nestedDropdown.classList.add('open');
              nestedLink.setAttribute('aria-expanded', 'true');
            });
            
            nestedDropdown.addEventListener('mouseleave', () => {
              nestedDropdown.classList.remove('open');
              nestedLink.setAttribute('aria-expanded', 'false');
            });
          }
        }
      });
    });
    
    // Close dropdowns when clicking outside (works for both desktop and touch)
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-nav-dropdown]')) {
        dropdowns.forEach(dropdown => {
          dropdown.classList.remove('open');
          const link = dropdown.querySelector('[data-nav-link-parent]');
          if (link) {
            link.setAttribute('aria-expanded', 'false');
          }
        });
        closeAllNestedDropdowns();
      }
    });
  }

  /**
   * Megamenu-3: show collection (or first product) image in [data-hover-image] when hovering or focusing nav links in [data-megamenu-hover-triggers]. Hides the image when the pointer or focus is outside that zone (but still in the panel), when the link has no data-megamenu-hover-image, or when leaving the panel.
   */
  initMegamenu3HoverImages() {
    const panels = this.querySelectorAll('[data-megamenu-hover-panel]');

    if (panels.length === 0) {
      return;
    }

    const updateImage = (panel, url) => {
      const img = panel.querySelector('[data-hover-image] img');

      if (!img || !url) {
        return;
      }

      img.src = url;
      img.alt = '';
      img.removeAttribute('hidden');
    };

    const clearImage = (panel) => {
      const img = panel.querySelector('[data-hover-image] img');

      if (!img) {
        return;
      }

      img.removeAttribute('src');
      img.setAttribute('hidden', '');
    };

    for (const panel of panels) {
      const linkZone = panel.querySelector('[data-megamenu-hover-triggers]');

      panel.addEventListener('pointerover', (e) => {
        if (!panel.contains(e.target)) {
          return;
        }

        if (!linkZone) {
          return;
        }

        if (!linkZone.contains(e.target)) {
          clearImage(panel);
          return;
        }

        const trigger = e.target.closest('a');

        if (!trigger || !linkZone.contains(trigger)) {
          return;
        }

        const url = trigger.getAttribute('data-megamenu-hover-image')?.trim();

        if (url) {
          updateImage(panel, url);
        } else {
          clearImage(panel);
        }
      });

      panel.addEventListener('pointerleave', (e) => {
        const next = e.relatedTarget;

        if (next && panel.contains(next)) {
          return;
        }

        clearImage(panel);
      });

      panel.addEventListener('focusin', (e) => {
        if (!panel.contains(e.target)) {
          return;
        }

        if (!linkZone) {
          return;
        }

        if (!linkZone.contains(e.target)) {
          clearImage(panel);
          return;
        }

        const trigger = e.target.closest('a');

        if (!trigger || !linkZone.contains(trigger)) {
          return;
        }

        const url = trigger.getAttribute('data-megamenu-hover-image')?.trim();

        if (url) {
          updateImage(panel, url);
        } else {
          clearImage(panel);
        }
      });

      panel.addEventListener('focusout', (e) => {
        const next = e.relatedTarget;

        if (next && panel.contains(next)) {
          if (linkZone && linkZone.contains(next)) {
            return;
          }

          clearImage(panel);
          return;
        }

        clearImage(panel);
      });
    }
  }

  /**
   * Handle mobile menu toggle button click
   * @param {Event} event - Click event
   */
  handleMenuToggle(event) {
    event.preventDefault();
    
    if (!this.mobileNavDrawer) {
      console.warn('ThemeHeader: Mobile navigation drawer not found');
      return;
    }
    
    // Toggle menu state
    this.isMenuOpen = !this.isMenuOpen;
    
    // Update button aria-expanded
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.setAttribute('aria-expanded', this.isMenuOpen.toString());
    }
    
    // Open or close drawer
    if (this.isMenuOpen) {
      if (typeof this.mobileNavDrawer.openDrawer === 'function') {
        this.mobileNavDrawer.openDrawer(this.mobileMenuToggle);
      } else {
        // Fallback if CustomDrawer methods don't exist
        this.mobileNavDrawer.classList.add('open');
        this.mobileNavDrawer.setAttribute('aria-hidden', 'false');
        if (this.mobileNavDrawer.manageInert) {
          this.mobileNavDrawer.removeAttribute('inert');
        }
        SiteOverlay.showOverlay();
        
        // Announce to screen reader
        const drawerName = this.mobileNavDrawer.getAttribute('aria-label') || 'Mobile navigation';
        Utility.announceToScreenReader(`${drawerName} drawer opened`);
      }
    } else {
      if (typeof this.mobileNavDrawer.closeDrawer === 'function') {
        this.mobileNavDrawer.closeDrawer();
      } else {
        // Fallback if CustomDrawer methods don't exist
        this.mobileNavDrawer.classList.remove('open');
        this.mobileNavDrawer.setAttribute('aria-hidden', 'true');
        if (this.mobileNavDrawer.manageInert) {
          this.mobileNavDrawer.setAttribute('inert', '');
        }
        SiteOverlay.hideOverlay();
        
        // Announce to screen reader
        const drawerName = this.mobileNavDrawer.getAttribute('aria-label') || 'Mobile navigation';
        Utility.announceToScreenReader(`${drawerName} drawer closed`);
      }
    }
  }

  /**
   * Handle drawer close event
   * Updates button state when drawer is closed externally
   */
  handleDrawerClose() {
    this.isMenuOpen = false;
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    }
  }
}

// Register custom element
customElements.define('theme-header', ThemeHeader);

/**
 * Mobile Navigation Component
 * Handles slide-in submenu navigation from right to left
 * @extends HTMLElement
 */
class MobileNavigation extends HTMLElement {
  constructor() {
    super();
    
    this.currentLevel = 'main';
    this.navLevels = [];
    this.toggleButtons = [];
    this.backButtons = [];
    this.accordionButtons = [];
    
    // Bind methods
    this.handleToggle = this.handleToggle.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleAccordionToggle = this.handleAccordionToggle.bind(this);
  }

  connectedCallback() {
    // Find all navigation levels
    this.navLevels = Array.from(this.querySelectorAll('.mobile-nav-level'));
    
    // Find all toggle buttons
    this.toggleButtons = Array.from(this.querySelectorAll('[data-mobile-nav-toggle]'));
    
    // Find all back buttons
    this.backButtons = Array.from(this.querySelectorAll('[data-mobile-nav-back]'));
    
    // Find all accordion toggle buttons
    this.accordionButtons = Array.from(this.querySelectorAll('[data-accordion-toggle]'));
    
    // Bind toggle buttons
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', this.handleToggle);
    });
    
    // Bind back buttons
    this.backButtons.forEach(button => {
      button.addEventListener('click', this.handleBack);
    });
    
    // Bind accordion buttons
    this.accordionButtons.forEach(button => {
      button.addEventListener('click', this.handleAccordionToggle);
    });
    
    // Initialize - show main level only
    this.showLevel('main');
  }

  disconnectedCallback() {
    this.toggleButtons.forEach(button => {
      button.removeEventListener('click', this.handleToggle);
    });
    
    this.backButtons.forEach(button => {
      button.removeEventListener('click', this.handleBack);
    });
    
    this.accordionButtons.forEach(button => {
      button.removeEventListener('click', this.handleAccordionToggle);
    });
  }

  /**
   * Handle submenu toggle - slide in from right
   * @param {Event} event - Click event
   */
  handleToggle(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const targetLevel = button.getAttribute('data-target-level');
    
    if (!targetLevel) return;
    
    // Update button aria-expanded
    button.setAttribute('aria-expanded', 'true');
    
    // Show target level
    this.showLevel(targetLevel);
    
    // Announce to screen reader
    const levelTitle = button.querySelector('span')?.textContent || 'Submenu';
    Utility.announceToScreenReader(`${levelTitle} submenu opened`);
  }

  /**
   * Handle back button - return to previous level
   * @param {Event} event - Click event
   */
  handleBack(event) {
    event.preventDefault();
    
    // Find current active level
    const currentLevel = this.querySelector('.mobile-nav-level.active');
    if (!currentLevel) return;
    
    // Reset toggle button that opened this level
    const currentLevelId = currentLevel.getAttribute('data-nav-level');
    const toggleButton = this.querySelector(`[data-target-level="${currentLevelId}"]`);
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }
    
    // Return to main level
    this.showLevel('main');
    
    // Announce to screen reader
    Utility.announceToScreenReader('Returned to main menu');
  }

  /**
   * Show specific navigation level
   * @param {string} levelId - Level identifier (main, child-X, etc.)
   */
  showLevel(levelId) {
    // Hide all levels
    this.navLevels.forEach(level => {
      level.classList.remove('active');
    });
    
    // Show target level
    const targetLevel = this.querySelector(`[data-nav-level="${levelId}"]`);
    if (targetLevel) {
      targetLevel.classList.add('active');
      this.currentLevel = levelId;
      
      // Focus first link in the new level
      const firstLink = targetLevel.querySelector('a, button');
      if (firstLink && typeof firstLink.focus === 'function') {
        setTimeout(() => firstLink.focus(), 100);
      }
    }
  }

  /**
   * Handle accordion toggle - expand/collapse third level menu
   * @param {Event} event - Click event
   */
  handleAccordionToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const accordionItem = button.closest('[data-accordion-item]');
    const content = accordionItem.querySelector('.accordion-content');
    
    if (!accordionItem || !content) return;
    
    const isOpen = accordionItem.classList.contains('open');
    
    if (isOpen) {
      // Close this accordion
      this.closeAccordion(accordionItem, button, content);
    } else {
      // Close other open accordions in the same level (accordion behavior)
      const currentLevel = accordionItem.closest('.mobile-nav-level');
      if (currentLevel) {
        const openAccordions = currentLevel.querySelectorAll('[data-accordion-item].open');
        openAccordions.forEach(item => {
          if (item !== accordionItem) {
            const itemButton = item.querySelector('[data-accordion-toggle]');
            const itemContent = item.querySelector('.accordion-content');
            if (itemButton && itemContent) {
              this.closeAccordion(item, itemButton, itemContent);
            }
          }
        });
      }
      
      // Open this accordion
      this.openAccordion(accordionItem, button, content);
    }
  }

  /**
   * Open accordion
   * @param {HTMLElement} accordionItem - Accordion container
   * @param {HTMLElement} button - Toggle button
   * @param {HTMLElement} content - Content element
   */
  openAccordion(accordionItem, button, content) {
    accordionItem.classList.add('open');
    button.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');
    content.removeAttribute('inert');
  }

  /**
   * Close accordion
   * @param {HTMLElement} accordionItem - Accordion container
   * @param {HTMLElement} button - Toggle button
   * @param {HTMLElement} content - Content element
   */
  closeAccordion(accordionItem, button, content) {
    accordionItem.classList.remove('open');
    button.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    content.setAttribute('inert', '');
  }

  /**
   * Reset to main level (called when drawer closes)
   */
  reset() {
    this.showLevel('main');
    
    // Reset all toggle buttons
    this.toggleButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
    });
    
    // Close all accordions
    const openAccordions = this.querySelectorAll('[data-accordion-item].open');
    openAccordions.forEach(accordionItem => {
      const button = accordionItem.querySelector('[data-accordion-toggle]');
      const content = accordionItem.querySelector('.accordion-content');
      if (button && content) {
        this.closeAccordion(accordionItem, button, content);
      }
    });
  }
}

/**
 * custom dropdown
 */

class customDropdown extends HTMLElement{
  constructor() {
    super();
    this.dropDownContainer = this.querySelector('.custom-dropdown');
    this.selectionList = this.querySelector('ul');
    this.defaultOption = this.querySelector('.default-value');
    this.optionsList = this.selectionList.querySelectorAll('li');

    this.optionsList.forEach(el => el.addEventListener('click', this.toggleList.bind(this)));
    this.defaultOption.addEventListener('click', this.showHideSelect.bind(this));

    this.addEventListener('click', e => e.stopPropagation());
    document.body.addEventListener('click', () => Utility.toggleElement(this.dropDownContainer, 'close'));
  }

  /**
   * show hide custom dropdown
   * @param e 
   */
  toggleList(e){
    let eTarget = e.currentTarget;
    this.optionsList.forEach(el => el.classList.remove('selected'));
    eTarget.classList.add('selected');
    this.defaultOption.innerHTML = eTarget.querySelector('.default_text').innerHTML;
    eTarget.closest('.custom-dropdown').classList.add('selected');
    Utility.toggleElement(this.dropDownContainer, 'close');
  }

  /**
   * show hide custom dropdown
   * @param e 
   */
    showHideSelect(e){
      let isOpen = this.dropDownContainer.classList.contains('open');
      isOpen ? Utility.toggleElement(this.dropDownContainer, 'close') : Utility.toggleElement(this.dropDownContainer, 'open');
    }
}
customElements.define('custom-dropdown', customDropdown);

// Register custom element
customElements.define('mobile-navigation', MobileNavigation);

// Initialize mobile navigation when drawer opens/closes
document.addEventListener('drawer:opened', (event) => {
  if (event.target.id === 'mobile-nav-drawer') {
    const mobileNav = event.target.closest('mobile-navigation');
    if (mobileNav && typeof mobileNav.reset === 'function') {
      mobileNav.reset();
    }
  }
});

document.addEventListener('drawer:closed', (event) => {
  if (event.target.id === 'mobile-nav-drawer') {
    const mobileNav = event.target.closest('mobile-navigation');
    if (mobileNav && typeof mobileNav.reset === 'function') {
      mobileNav.reset();
    }
  }
});


/**
 * Quantity Input 
 */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.quantityUpdate) {
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = parseInt(this.input.value, 10);
    const min = parseInt(this.input.min || this.input.dataset.min || '0', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : null;
    const step = parseInt(this.input.step || '1', 10);
    const current = Number.isNaN(previousValue) ? min : previousValue;
    const direction = event.currentTarget.name === 'plus' ? 1 : -1;

    let nextValue = current + (step * direction);
    nextValue = Math.max(nextValue, min);

    if (max !== null && !Number.isNaN(max)) {
      nextValue = Math.min(nextValue, max);
    }

    this.input.value = nextValue;

    if (previousValue !== nextValue) {
      this.input.dispatchEvent(this.changeEvent);
    } else {
      this.validateQtyRules();
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value, 10);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const disableMinus = parseInt(value, 10) <= parseInt(this.input.min, 10);
      buttonMinus.classList.toggle('is-disabled', disableMinus);
      buttonMinus.disabled = disableMinus;
    }
    if (this.input.max) {
      const max = parseInt(this.input.max, 10);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      const disablePlus = parseInt(value, 10) >= max;
      buttonPlus.classList.toggle('is-disabled', disablePlus);
      buttonPlus.disabled = disablePlus;
    }
  }
}

customElements.define('quantity-input', QuantityInput);

/**
 * Mobile sticky ATC bar (product page)
 * Pins `.mobile-sticky-atc-btn` to the viewport bottom for <= 767px,
 * only after it scrolls out of view.
 */
(function initMobileStickyAtc() {
  const stickyButtons = Array.from(document.querySelectorAll('.product-block .mobile-sticky-atc-btn'));
  if (stickyButtons.length === 0) return;

  const mobileQuery = window.matchMedia('(max-width: 767px)');

  const state = new Map(); // el -> { sentinel, isSticky }
  let observer;

  const ensureSentinel = (el) => {
    const existing = el.previousElementSibling;
    if (existing && existing.classList.contains('mobile-sticky-atc-sentinel')) return existing;

    const sentinel = document.createElement('div');
    sentinel.className = 'mobile-sticky-atc-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.height = '0px';
    sentinel.style.width = '1px';
    el.parentNode.insertBefore(sentinel, el);
    return sentinel;
  };

  const updateGlobalOffset = () => {
    const stickyEls = stickyButtons.filter((el) => state.get(el)?.isSticky);
    const isAnySticky = stickyEls.length > 0;

    document.body.classList.toggle('has-mobile-sticky-atc', isAnySticky);

    if (isAnySticky) {
      const heights = stickyEls.map((el) => el.getBoundingClientRect().height);
      const maxHeight = Math.max(0, ...heights);
      document.documentElement.style.setProperty('--mobile-sticky-atc-height', `${maxHeight}px`);
    } else {
      document.documentElement.style.setProperty('--mobile-sticky-atc-height', '0px');
    }
  };

  const setSticky = (el, makeSticky) => {
    const entry = state.get(el);
    if (!entry) return;
    if (entry.isSticky === makeSticky) return;

    entry.isSticky = makeSticky;
    el.classList.toggle('is-mobile-sticky', makeSticky);
    updateGlobalOffset();
  };

  const enable = () => {
    stickyButtons.forEach((el) => {
      const sentinel = ensureSentinel(el);
      state.set(el, { sentinel, isSticky: false });
      el.classList.remove('is-mobile-sticky');
    });

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = stickyButtons.find((b) => state.get(b)?.sentinel === entry.target);
          if (!el) continue;

          // When the sentinel is visible, keep in normal flow.
          // When it scrolls out of view, make the bar sticky.
          setSticky(el, !entry.isIntersecting);
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    stickyButtons.forEach((el) => observer.observe(state.get(el).sentinel));
    updateGlobalOffset();
  };

  const disable = () => {
    if (observer) observer.disconnect();
    observer = undefined;

    stickyButtons.forEach((el) => {
      const entry = state.get(el);
      if (entry) entry.isSticky = false;
      el.classList.remove('is-mobile-sticky');
    });

    document.body.classList.remove('has-mobile-sticky-atc');
    document.documentElement.style.setProperty('--mobile-sticky-atc-height', '0px');
  };

  const sync = () => {
    if (mobileQuery.matches) {
      if (!observer) enable();
      updateGlobalOffset();
    } else {
      disable();
    }
  };

  // Initial sync + listeners
  sync();
  window.addEventListener('resize', debounce(sync, 120));

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', sync);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(sync);
  }
})();